// Relation members from the flat `relation-member-<n>-*` properties gss-wfs writes.
// Roles with a range keep the role as tagged (`road:step=1900-13594`); the range is
// read out next to it. Other members carry `lr-start` (+ `lr-end`) / `lr-type`.

const MEMBER_KEY = /^relation-member-(\d+)-(id|role|lr-start|lr-end|lr-type)$/;
const ROLE_RANGE = /^(\d+)(?:-(\d*))?$/;
const LR_TYPES = { step: "step", linear: "linear", spot: "spot" };

/**
 * `name:kind=a-b;c-d` → {roleName, lr}; anything else is a plain role name.
 * @param {string} role
 */
function parseRole(role) {
  const match = /^([^:=]+):(step|linear|spot)=(.*)$/.exec(role);
  if (!match) return { roleName: role, lr: null };
  const ranges = [];
  for (const part of match[3].split(";")) {
    const range = ROLE_RANGE.exec(part);
    if (!range) return { roleName: match[1], lr: null };
    ranges.push({
      startCm: Number(range[1]),
      endCm: range[2] ? Number(range[2]) : null,
    });
  }
  const lr = { type: match[2], ...ranges[0] };
  // Only the multi-range case needs the list; one range is the lr itself.
  if (ranges.length > 1) lr.ranges = ranges;
  return { roleName: match[1], lr };
}

/**
 * @param {Record<string, unknown>} properties
 * @returns {{index: number, id: string, role: string, roleName: string,
 *   lr: {type: "step"|"linear"|"spot", startCm: number, endCm: number|null}|null}[]}
 */
export function parseMembers(properties) {
  const byIndex = new Map();
  Object.entries(properties).forEach(([key, value]) => {
    const match = MEMBER_KEY.exec(key);
    if (!match) return;
    const index = Number(match[1]);
    if (!byIndex.has(index)) byIndex.set(index, {});
    byIndex.get(index)[match[2]] = value;
  });
  return [...byIndex.keys()]
    .sort((a, b) => a - b)
    .map((index) => {
      const raw = byIndex.get(index);
      const role = raw.role === undefined ? "" : String(raw.role);
      const parsed = parseRole(role);
      const lrType = LR_TYPES[String(raw["lr-type"] ?? "").toLowerCase()];
      const start = Number(raw["lr-start"]);
      const end = Number(raw["lr-end"]);
      const hasEnd = raw["lr-end"] !== undefined && Number.isFinite(end);
      const lr =
        parsed.lr ??
        (raw["lr-start"] !== undefined && Number.isFinite(start)
          ? {
              type: lrType ?? (hasEnd ? "step" : "spot"),
              startCm: start,
              endCm: hasEnd ? end : null,
            }
          : null);
      return {
        index,
        id: raw.id === undefined ? "" : String(raw.id),
        role,
        roleName: parsed.roleName,
        lr,
      };
    });
}
